import { ILocationNode } from '@app/shared/ui-components/au-locations-tree-selector/location-tree-selector.service';
import { IActionCenterLinker } from '../../interfaces';
import { IAssigneeOrganization } from '@app/shared/interfaces/selectable-options.interfaces';
import { Apollo } from 'apollo-angular';
import {
	ILinkLabelsVariables,
	ILinkWorkOrdersActionCenterVariables,
} from './linking-interfaces';
import { firstValueFrom, timeout } from 'rxjs';
import { linkLabelsActionCenter, linkWorkOrdersActionCenter } from './mutations';
//import { IAssetInput } from '@app/pages/cmms/interfaces/files.interfaces';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import { IWorkOrderRoutine } from '@app/domain/cmms-types/workorder-types';

export class WorkOrderLocationLinker implements IActionCenterLinker<ILocationNode> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: ILocationNode[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			locations: targets,
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderCategoryLinker implements IActionCenterLinker<string> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: string[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			category: targets[0],
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderPriorityLinker implements IActionCenterLinker<number> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: number[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			priority: targets[0],
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderStatusLinker implements IActionCenterLinker<string> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: string[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			status: targets[0],
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderDueDateLinker implements IActionCenterLinker<Date> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: Date[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			deadline: targets[0],
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderScheduleLinker implements IActionCenterLinker<IWorkOrderRoutine> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IWorkOrderRoutine[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			routineData: targets[0],
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class workOrderAssigneeLinker
	implements IActionCenterLinker<IAssigneeOrganization>
{
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IAssigneeOrganization[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			assignee: targets[0].psId,
			assigneeType: targets[0].contentType,
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class workOrderApproverLinker
	implements IActionCenterLinker<IAssigneeOrganization>
{
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IAssigneeOrganization[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			approver: targets[0].psId,
			approverType: targets[0].contentType,
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderAssetLinker implements IActionCenterLinker<ICmmsAsset> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: ICmmsAsset[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			assets: targets.map(asset => ({ id: asset.pk, name: asset.name })),
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderIncidentLinker implements IActionCenterLinker<IIncident> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IIncident[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			incidents: targets.map(incident => incident.pk),
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderRequestLinker implements IActionCenterLinker<IRequest> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IRequest[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			requests: targets.map(request => request.pk),
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderFileLinker implements IActionCenterLinker<IFileEntity> {
	apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: IFileEntity[]): Promise<any> {
		const variables: ILinkWorkOrdersActionCenterVariables = {
			workOrders: selectedPks,
			files: targets.map(file => file.pk),
		};

		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkWorkOrdersActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}

export class WorkOrderLabelLinker implements IActionCenterLinker<string> {
	private readonly apollo: Apollo;

	constructor(deps: { apollo: Apollo }) {
		this.apollo = deps.apollo;
	}

	async link(selectedPks: string[], targets: string[]): Promise<any> {
		const variables: ILinkLabelsVariables = {
			workOrderIds: selectedPks,
			labelIds: targets,
		};
		return await firstValueFrom(
			this.apollo
				.mutate({ mutation: linkLabelsActionCenter, variables })
				.pipe(timeout(5000))
		);
	}
}
